/**
 * Copyright (c) 2010-2023 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import {
    AfterContentInit,
    Directive,
    ElementRef, EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit, Output,
    Renderer2,
    SimpleChange,
    SimpleChanges
} from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { ActiveToolState } from './state/active-tool.state';
import { Observable, Subject } from 'rxjs';
import { LoggerService } from '../../../shared/services/logger.service';
import { SetActiveTool } from './state/active-tool.actions';
import { takeUntil } from 'rxjs/operators';

@Directive({
    selector: `[csActiveTool]`,
})
export class ActiveToolDirective implements OnInit, OnChanges, AfterContentInit, OnDestroy {

    @Input('csActiveTool') activeToolName: string;
    @Input() deactivate$: Observable<void>;

    @Output() deactivated = new EventEmitter<void>();

    @Select(ActiveToolState.activeTool) activeTool$: Observable<string>;

    private isActive = false;
    private disposeSubject: Subject<void>;
    private dispose$: Observable<void>;

    constructor(
        private logger: LoggerService,
        private el: ElementRef,
        private renderer: Renderer2,
        private store: Store,
    ) {
        this.disposeSubject = new Subject<void>();
        this.dispose$ = this.disposeSubject.asObservable();
    }

    ngOnInit() {
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.deactivate$) {
            const change: SimpleChange = changes.deactivate$;
            if (change.currentValue && change.currentValue !== change.previousValue) {
                this.deactivate$.pipe(
                    takeUntil(this.dispose$),
                ).subscribe(() => {
                    if (this.isActive) {
                        this.store.dispatch(new SetActiveTool({name: 'Pan'}));
                    }
                });
            }
        }
    }

    ngAfterContentInit() {
        this.activeTool$.pipe(
            takeUntil(this.dispose$),
        ).subscribe((toolName) => {
            if (this.isActive) {
                this.renderer.removeClass(this.el.nativeElement, 'active-tool');
            }
            this.isActive = (toolName && toolName === this.activeToolName);
            if (this.isActive) {
                this.renderer.addClass(this.el.nativeElement, 'active-tool');
            } else {
                this.deactivated.emit();
            }
        });
    }

    ngOnDestroy() {
        this.disposeSubject.next();
    }

    @HostListener('click', ['$event'])
    onClick(evt: MouseEvent) {
        this.store.dispatch(new SetActiveTool({name: this.activeToolName}));
    }
}
